import { observer } from 'mobx-react-lite';
import React from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';
import { getAddressModalParams } from '~/utils/addressModal';

import { DESKTOP_HEADER_ID } from '../Header/constants';
import { HeaderDubaiExpressAddress } from './HeaderDubaiExpressAddress';
import { HeaderDubaiExpressCategories } from './HeaderDubaiExpressCategories';
import { HeaderDubaiExpressNavigation } from './HeaderDubaiExpressNavigation';

type HeaderDubaiExpressProps = Omit<
  React.ComponentPropsWithRef<'header'>,
  'children' | 'id'
>;

/**
 * Standalone, always-solid (white) header for DubaiExpress.
 *
 * Layout: a top bar with the delivery address (left), the brand logo (center,
 * desktop only) and the user navigation (right), followed by a single-line,
 * horizontally scrollable row of category links. Kept separate from the
 * MCosmetic header so its styling and behaviour can evolve independently.
 */
const HeaderDubaiExpress = React.forwardRef<
  HTMLElement,
  HeaderDubaiExpressProps
>(({ className, ...props }, ref) => {
  const { isMobile } = useGlobal();
  const { openModal } = useModal();

  // The destination is chosen at checkout, so the header carries no address.
  const showAddress = !company.addressSelectedAtCheckout;

  const handleDeliveryAddress = () => {
    const [targetModal, source, params] = getAddressModalParams('header', {
      [MODAL_EDIT_ADDRESS_PARAM]: userStore?.deliveryAddress?.addressId || '',
    });

    openModal(targetModal, source, params);
  };

  return (
    <header
      {...props}
      ref={ref}
      id={DESKTOP_HEADER_ID}
      data-scrolled="true"
      data-company={company.name}
      className={cn(
        'group relative z-12 flex w-full flex-col gap-3 border-b border-b-navigation-stroke-tertiary bg-navigation-bg text-navigation-text-primary',
        'px-4 pt-safe-offset-4 pb-3 md:fixed md:inset-x-0 md:gap-4 md:px-10 md:pt-6 md:pb-4',
        className,
      )}
    >
      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-6">
        <div className="flex min-w-0 truncate">
          {showAddress ? (
            <HeaderDubaiExpressAddress onClick={handleDeliveryAddress} />
          ) : (
            isMobile && (
              <Link to="/" aria-label="logo">
                <img
                  src={company.images.logo.dark}
                  alt="logo"
                  className="h-6 w-auto max-w-45 object-contain"
                />
              </Link>
            )
          )}
        </div>
        {!isMobile && (
          <>
            <Link to="/" className="justify-self-center" aria-label="logo">
              <img
                src={company.images.logo.dark}
                alt="logo"
                className="h-[27px] w-auto max-w-50 object-contain"
              />
            </Link>
            <div className="flex shrink-0 items-center gap-5 justify-self-end text-navigation-text-primary">
              <HeaderDubaiExpressNavigation />
            </div>
          </>
        )}
      </div>
      <HeaderDubaiExpressCategories />
    </header>
  );
});
HeaderDubaiExpress.displayName = 'HeaderDubaiExpress';

export default observer(HeaderDubaiExpress);
